import { ApiError, apiFetch } from "@pontimed/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * E-1 (launch-readiness): client surface for the ConsentRecord API.
 * GET/POST /api/account/consent — grant/revoke with policyVersion audit trail
 * (IP + user-agent captured server-side).
 */

export type ConsentType =
  | "terms_of_service"
  | "privacy_policy"
  | "health_data_processing"
  | "marketing_email"
  | "marketing_sms"
  | "marketing_viber"
  | "ai_processing"
  | "pharma_ads"
  | "data_analytics"
  | "cross_border_data"
  | "third_party_sharing"
  | "research_anonymized";

export interface ConsentStatus {
  consentType: ConsentType;
  granted: boolean;
  grantedAt: string | null;
}

export interface ConsentsResponse {
  consents: ConsentStatus[];
  /**
   * The account has a real, verified email address — the only address a
   * `marketing_email` grant covers (the send gate refuses any other). False
   * for an unverified address or a platform placeholder (phone-only and some
   * OAuth sign-ins); POST refuses that grant with 409 `email_not_verified`.
   * Absent on an older API: treat as usable.
   */
  emailUsable?: boolean;
  /** Same for a verified phone number — `marketing_sms` / `marketing_viber` (409 `phone_not_verified`). */
  phoneUsable?: boolean;
}

/** Bump when counsel revises the consent wording (Art. 7(1) proof). */
export const CONSENT_POLICY_VERSION = "2026-06-11-draft";

export const CONSENTS_QUERY_KEY = ["api", "/api/account/consent"] as const;

export function useConsents(options?: {
  /**
   * Per-observer freshness. Default 60 s; the marketing switches pass 0 so a
   * return from verifying an address (another tab or page) re-reads
   * `emailUsable` / `phoneUsable` on mount and focus.
   */
  staleTime?: number;
}) {
  return useQuery({
    queryKey: CONSENTS_QUERY_KEY,
    queryFn: () => apiFetch.get<ConsentsResponse>("/api/account/consent"),
    staleTime: options?.staleTime ?? 60_000,
    // Override the app-wide refetchOnMount/refetchOnWindowFocus: false.
    // Consent drives a blocking Art. 9 gate — a tab that cached
    // granted=false before the user accepted in ANOTHER tab would otherwise
    // re-prompt forever (the cache is per-tab and nothing invalidates it).
    // Stale-on-mount/focus refetch lets old tabs self-heal after a grant.
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}

/** Tri-state: undefined while loading, then boolean. */
export function useConsentGranted(type: ConsentType): boolean | undefined {
  const { data, isLoading } = useConsents();
  if (isLoading || !data) return undefined;
  return data.consents.find((c) => c.consentType === type)?.granted ?? false;
}

/**
 * Why POST /consent refused a marketing grant: 409 when the address the grant
 * would cover is not the account's own verified one. Revokes are never refused.
 */
export type ConsentGrantRefusal = "email_not_verified" | "phone_not_verified";

export function readConsentGrantRefusal(error: unknown): ConsentGrantRefusal | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const body = error.body;
  if (!body || typeof body !== "object") return null;
  const code = (body as { error?: unknown }).error;
  return code === "email_not_verified" || code === "phone_not_verified" ? code : null;
}

export function useConsentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      consentType,
      action,
    }: {
      consentType: ConsentType;
      action: "grant" | "revoke";
    }) =>
      apiFetch.post("/api/account/consent", {
        consentType,
        action,
        policyVersion: CONSENT_POLICY_VERSION,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONSENTS_QUERY_KEY }),
    // The server judged the address unusable where the cached flags did not
    // (stale, or an older response without them): re-read them so the switch
    // locks and says why.
    onError: (error) => {
      if (readConsentGrantRefusal(error)) {
        void queryClient.invalidateQueries({ queryKey: CONSENTS_QUERY_KEY });
      }
    },
  });
}
